import React, { useState } from 'react';
import {
  XMarkIcon,
  PlayIcon,
  PauseCircleIcon,
  TrashIcon,
  DocumentArrowDownIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import type { Tenant } from './TenantTable';

export type BulkOperation = 
  | 'activate'
  | 'suspend' 
  | 'delete'
  | 'export'
  | 'message'
  | 'plan_change'
  | 'feature_toggle';

interface BulkOperationJob {
  id: string;
  operation: BulkOperation;
  tenantIds: string[];
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  totalItems: number;
  processedItems: number;
  failedItems: number;
  createdAt: Date;
  completedAt?: Date;
  error?: string;
}

interface BulkOperationConfig {
  operation: BulkOperation;
  params?: Record<string, any>;
  confirmationMessage?: string;
  scheduleFor?: Date;
}

interface BulkOperationsPanelProps {
  isOpen: boolean;
  selectedTenants: string[];
  tenants: Tenant[];
  onClose: () => void;
  onExecute: (config: BulkOperationConfig) => void;
}

const BulkOperationsPanel: React.FC<BulkOperationsPanelProps> = ({
  isOpen,
  selectedTenants,
  tenants,
  onClose,
  onExecute
}) => {
  const [selectedOperation, setSelectedOperation] = useState<BulkOperation | null>(null);
  const [operationParams, setOperationParams] = useState<Record<string, any>>({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [scheduleFor, setScheduleFor] = useState<string>('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [activeJobs, setActiveJobs] = useState<BulkOperationJob[]>([]);

  const operations = [
    {
      id: 'activate' as const,
      label: 'Activate Tenants',
      description: 'Reactivate suspended tenant accounts',
      icon: PlayIcon,
      color: 'text-green-600 bg-green-50 border-green-200',
      dangerous: false,
      requiresParams: false
    },
    {
      id: 'suspend' as const,
      label: 'Suspend Tenants',
      description: 'Temporarily suspend tenant accounts',
      icon: PauseCircleIcon,
      color: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      dangerous: true,
      requiresParams: true,
      params: ['reason', 'gracePeriod']
    },
    {
      id: 'delete' as const,
      label: 'Delete Tenants',
      description: 'Permanently delete tenant accounts and data',
      icon: TrashIcon,
      color: 'text-red-600 bg-red-50 border-red-200',
      dangerous: true,
      requiresParams: true,
      params: ['reason', 'confirmDelete']
    },
    {
      id: 'export' as const,
      label: 'Export Data',
      description: 'Export tenant data to various formats',
      icon: DocumentArrowDownIcon,
      color: 'text-blue-600 bg-blue-50 border-blue-200',
      dangerous: false,
      requiresParams: true,
      params: ['format', 'includeData']
    },
    {
      id: 'message' as const,
      label: 'Send Message',
      description: 'Send bulk messages to tenant administrators',
      icon: ChatBubbleLeftRightIcon,
      color: 'text-purple-600 bg-purple-50 border-purple-200',
      dangerous: false,
      requiresParams: true,
      params: ['subject', 'message', 'messageType']
    },
    {
      id: 'plan_change' as const,
      label: 'Change Plans',
      description: 'Bulk update subscription plans',
      icon: Cog6ToothIcon,
      color: 'text-indigo-600 bg-indigo-50 border-indigo-200',
      dangerous: false,
      requiresParams: true,
      params: ['newPlan', 'prorationMode']
    },
    {
      id: 'feature_toggle' as const,
      label: 'Toggle Features',
      description: 'Enable or disable features across tenants',
      icon: Cog6ToothIcon,
      color: 'text-gray-600 bg-gray-50 border-gray-200',
      dangerous: false,
      requiresParams: true,
      params: ['features', 'action']
    }
  ];

  const selectedTenantData = tenants.filter(t => selectedTenants.includes(t.id));
  const selectedOp = operations.find(op => op.id === selectedOperation);

  const handleOperationSelect = (operation: BulkOperation) => {
    setSelectedOperation(operation);
    setOperationParams({});
  };

  const handleParamUpdate = (key: string, value: any) => {
    setOperationParams(prev => ({ ...prev, [key]: value }));
  };

  const validateParams = (): boolean => {
    if (!selectedOp?.requiresParams) return true;
    
    return selectedOp.params?.every(param => {
      const value = operationParams[param];
      return value !== undefined && value !== '' && value !== null;
    }) || false;
  };

  const getConfirmationMessage = (): string => {
    const tenantCount = selectedTenants.length;
    const tenantText = tenantCount === 1 ? 'tenant' : 'tenants';
    
    switch (selectedOperation) {
      case 'activate':
        return `Are you sure you want to activate ${tenantCount} ${tenantText}?`;
      case 'suspend':
        return `Are you sure you want to suspend ${tenantCount} ${tenantText}? This will prevent users from accessing their accounts.`;
      case 'delete':
        return `Are you sure you want to DELETE ${tenantCount} ${tenantText}? This action CANNOT be undone and will permanently remove all data.`;
      case 'export':
        return `Export data for ${tenantCount} ${tenantText} in ${operationParams.format} format?`;
      case 'message':
        return `Send "${operationParams.subject}" message to ${tenantCount} ${tenantText}?`;
      case 'plan_change':
        return `Change ${tenantCount} ${tenantText} to ${operationParams.newPlan} plan?`;
      case 'feature_toggle':
        return `${operationParams.action} features for ${tenantCount} ${tenantText}?`;
      default:
        return `Execute operation on ${tenantCount} ${tenantText}?`;
    }
  };

  const handleExecute = async () => {
    if (!selectedOperation || !validateParams()) return;

    setIsExecuting(true);
    try {
      const config: BulkOperationConfig = {
        operation: selectedOperation,
        params: operationParams,
        confirmationMessage: getConfirmationMessage(),
        scheduleFor: scheduleFor ? new Date(scheduleFor) : undefined
      };

      // Create a job tracking entry
      const job: BulkOperationJob = {
        id: `job_${Date.now()}`,
        operation: selectedOperation,
        tenantIds: selectedTenants,
        status: 'pending',
        progress: 0,
        totalItems: selectedTenants.length,
        processedItems: 0,
        failedItems: 0,
        createdAt: new Date()
      };

      setActiveJobs(prev => [...prev, job]);
      
      // Simulate job progress
      const progressInterval = setInterval(() => {
        setActiveJobs(prev => prev.map(j => {
          if (j.id === job.id && j.status === 'running') {
            const newProgress = Math.min(j.progress + Math.random() * 20, 100);
            return {
              ...j,
              progress: newProgress,
              processedItems: Math.floor((newProgress / 100) * j.totalItems),
              status: newProgress >= 100 ? 'completed' : 'running'
            };
          }
          return j;
        }));
      }, 1000);

      // Start the job
      setTimeout(() => {
        setActiveJobs(prev => prev.map(j => 
          j.id === job.id ? { ...j, status: 'running' } : j
        ));
      }, 500);

      // Complete the job
      setTimeout(() => {
        clearInterval(progressInterval);
        setActiveJobs(prev => prev.map(j => 
          j.id === job.id ? { 
            ...j, 
            status: 'completed', 
            progress: 100,
            processedItems: j.totalItems,
            completedAt: new Date()
          } : j
        ));
      }, 5000);

      await onExecute(config);
      
      toast.success(`Bulk ${selectedOperation} operation started`);
      setShowConfirmation(false);
    } catch (error) {
      toast.error('Failed to start bulk operation');
    } finally {
      setIsExecuting(false);
    }
  };

  const renderParameterInputs = () => {
    if (!selectedOp?.requiresParams || !selectedOp.params) return null;

    return (
      <div className="space-y-4">
        {selectedOp.params.map(param => {
          switch (param) {
            case 'reason':
              return (
                <div key={param}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason *
                  </label>
                  <textarea
                    rows={3}
                    value={operationParams.reason || ''}
                    onChange={(e) => handleParamUpdate('reason', e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    placeholder="Enter reason for this action..."
                  />
                </div>
              );
            
            case 'gracePeriod':
              return (
                <div key={param}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Grace Period (days)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={operationParams.gracePeriod || 0}
                    onChange={(e) => handleParamUpdate('gracePeriod', parseInt(e.target.value))}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                </div>
              );

            case 'confirmDelete':
              return (
                <div key={param}>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={operationParams.confirmDelete || false}
                      onChange={(e) => handleParamUpdate('confirmDelete', e.target.checked)}
                      className="rounded border-gray-300 text-red-600 shadow-sm focus:border-red-300 focus:ring focus:ring-red-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-red-700">
                      I confirm that I want to permanently delete these tenants
                    </span>
                  </label>
                </div>
              );

            case 'format':
              return (
                <div key={param}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Export Format *
                  </label>
                  <select
                    value={operationParams.format || ''}
                    onChange={(e) => handleParamUpdate('format', e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  >
                    <option value="">Select format</option>
                    <option value="csv">CSV</option>
                    <option value="json">JSON</option>
                    <option value="xlsx">Excel</option>
                  </select>
                </div>
              );

            case 'includeData':
              return (
                <div key={param}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Include Data
                  </label>
                  <div className="space-y-2">
                    {['basic_info', 'users', 'analytics', 'billing'].map(dataType => (
                      <label key={dataType} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={(operationParams.includeData || []).includes(dataType)}
                          onChange={(e) => {
                            const current = operationParams.includeData || [];
                            const updated = e.target.checked
                              ? [...current, dataType]
                              : current.filter((item: string) => item !== dataType);
                            handleParamUpdate('includeData', updated);
                          }}
                          className="rounded border-gray-300 text-blue-600 shadow-sm"
                        />
                        <span className="ml-2 text-sm text-gray-700 capitalize">
                          {dataType.replace('_', ' ')}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );

            case 'subject':
              return (
                <div key={param}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message Subject *
                  </label>
                  <input
                    type="text"
                    value={operationParams.subject || ''}
                    onChange={(e) => handleParamUpdate('subject', e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    placeholder="Important system update"
                  />
                </div>
              );

            case 'message':
              return (
                <div key={param}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message Content *
                  </label>
                  <textarea
                    rows={4}
                    value={operationParams.message || ''}
                    onChange={(e) => handleParamUpdate('message', e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    placeholder="Enter your message here..."
                  />
                </div>
              );

            case 'messageType':
              return (
                <div key={param}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message Type
                  </label>
                  <select
                    value={operationParams.messageType || 'announcement'}
                    onChange={(e) => handleParamUpdate('messageType', e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  >
                    <option value="announcement">Announcement</option>
                    <option value="maintenance">Maintenance Notice</option>
                    <option value="billing">Billing Update</option>
                    <option value="support">Support Message</option>
                  </select>
                </div>
              );

            default:
              return null;
          }
        })}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Bulk Operations</h3>
                <p className="text-sm text-gray-500">
                  {selectedTenants.length} tenant{selectedTenants.length !== 1 ? 's' : ''} selected
                </p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="flex">
            {/* Operations List */}
            <div className="w-1/3 border-r border-gray-200 p-6">
              <h4 className="text-sm font-medium text-gray-900 mb-4">Select Operation</h4>
              <div className="space-y-2">
                {operations.map((operation) => (
                  <button
                    key={operation.id}
                    onClick={() => handleOperationSelect(operation.id)}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      selectedOperation === operation.id
                        ? operation.color
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <operation.icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-sm">{operation.label}</div>
                        <div className="text-xs text-gray-600 mt-1">{operation.description}</div>
                        {operation.dangerous && (
                          <div className="flex items-center mt-1">
                            <ExclamationTriangleIcon className="h-3 w-3 text-red-500 mr-1" />
                            <span className="text-xs text-red-600">Destructive</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Configuration Panel */}
            <div className="flex-1 p-6">
              {selectedOperation ? (
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">
                      {selectedOp?.label}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {selectedOp?.description}
                    </p>
                  </div>

                  {/* Selected Tenants Preview */}
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">
                      Selected Tenants ({selectedTenants.length})
                    </h5>
                    <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                      <div className="space-y-1">
                        {selectedTenantData.slice(0, 10).map((tenant) => (
                          <div key={tenant.id} className="text-sm text-gray-800">
                            {tenant.name} ({tenant.domain})
                          </div>
                        ))}
                        {selectedTenantData.length > 10 && (
                          <div className="text-sm text-gray-500">
                            ... and {selectedTenantData.length - 10} more
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Parameters */}
                  {renderParameterInputs()}

                  {/* Scheduling */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Schedule Operation (Optional)
                    </label>
                    <input
                      type="datetime-local"
                      value={scheduleFor}
                      onChange={(e) => setScheduleFor(e.target.value)}
                      min={new Date().toISOString().slice(0, 16)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Leave empty to execute immediately
                    </p>
                  </div>

                  {/* Confirmation */}
                  {showConfirmation && (
                    <div className={`border-2 rounded-lg p-4 ${
                      selectedOp?.dangerous 
                        ? 'border-red-200 bg-red-50' 
                        : 'border-blue-200 bg-blue-50'
                    }`}>
                      <div className="flex items-start space-x-3">
                        {selectedOp?.dangerous ? (
                          <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mt-0.5" />
                        ) : (
                          <CheckCircleIcon className="h-5 w-5 text-blue-500 mt-0.5" />
                        )}
                        <div>
                          <h5 className={`font-medium ${
                            selectedOp?.dangerous ? 'text-red-800' : 'text-blue-800'
                          }`}>
                            Confirm Operation
                          </h5>
                          <p className={`text-sm mt-1 ${
                            selectedOp?.dangerous ? 'text-red-700' : 'text-blue-700'
                          }`}>
                            {getConfirmationMessage()}
                          </p>
                          {scheduleFor && (
                            <p className={`text-sm mt-1 ${
                              selectedOp?.dangerous ? 'text-red-600' : 'text-blue-600'
                            }`}>
                              Scheduled for: {new Date(scheduleFor).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Cog6ToothIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Select an Operation</h4>
                  <p className="text-gray-600">
                    Choose an operation from the list to configure and execute on selected tenants.
                  </p>
                </div>
              )}

              {/* Active Jobs */}
              {activeJobs.length > 0 && (
                <div className="mt-6">
                  <h5 className="text-sm font-medium text-gray-700 mb-3">Active Jobs</h5>
                  <div className="space-y-2">
                    {activeJobs.map((job) => (
                      <div key={job.id} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            {job.status === 'running' ? (
                              <ClockIcon className="h-4 w-4 text-blue-500 animate-spin" />
                            ) : job.status === 'completed' ? (
                              <CheckCircleIcon className="h-4 w-4 text-green-500" />
                            ) : (
                              <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />
                            )}
                            <span className="text-sm font-medium capitalize">
                              {job.operation.replace('_', ' ')}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {job.processedItems}/{job.totalItems}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all ${
                              job.status === 'completed' ? 'bg-green-500' : 
                              job.status === 'failed' ? 'bg-red-500' : 'bg-blue-500'
                            }`}
                            style={{ width: `${job.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 flex justify-between">
            <button
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Close
            </button>
            
            {selectedOperation && (
              <div className="flex space-x-3">
                {showConfirmation && (
                  <button
                    onClick={() => setShowConfirmation(false)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Back
                  </button>
                )}
                
                <button
                  onClick={showConfirmation ? handleExecute : () => setShowConfirmation(true)}
                  disabled={!validateParams() || isExecuting}
                  className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                    selectedOp?.dangerous 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isExecuting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Executing...
                    </>
                  ) : showConfirmation ? (
                    <>
                      <CheckCircleIcon className="h-4 w-4 mr-2" />
                      Execute Now
                    </>
                  ) : (
                    'Configure & Execute'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkOperationsPanel;